(() => {
  const RESULT_KEYS = ['lust', 'disturbance', 'excess', 'chaos', 'ordinary'];
  const STORAGE_KEY = 'gp_interrogation_result';
  const STORAGE_VERSION = 1;
  const ADVANCE_DELAY = 400;

  const QUESTIONS = [
    {
      prompt: 'What do you want your outfit to say?',
      answers: [
        { label: 'Come closer, but be careful.', primary: 'lust', secondary: 'chaos' },
        { label: 'You’re going to notice me.', primary: 'disturbance', secondary: 'chaos' },
        { label: 'I came to do the most.', primary: 'excess', secondary: 'lust' },
        { label: 'Something dangerous is happening.', primary: 'chaos', secondary: 'ordinary' },
        { label: 'You haven’t seen this before.', primary: 'ordinary', secondary: 'chaos' },
      ],
    },
    {
      prompt: 'Pick your style energy.',
      answers: [
        { label: 'Flirty and dangerous.', primary: 'lust', secondary: 'excess' },
        { label: 'Loud and impossible to ignore.', primary: 'disturbance', secondary: 'chaos' },
        { label: 'Extra in the best way.', primary: 'excess', secondary: 'chaos' },
        { label: 'Dark and chaotic.', primary: 'chaos', secondary: 'ordinary' },
        { label: 'Different from everyone else.', primary: 'ordinary', secondary: 'excess' },
      ],
    },
    {
      prompt: 'Which design would you choose first?',
      answers: [
        { label: 'Kiss marks, red graphics, or flirty details.', primary: 'lust', secondary: 'ordinary' },
        { label: 'Huge graphics and bold text.', primary: 'disturbance', secondary: 'chaos' },
        { label: 'Rich textures and statement pieces.', primary: 'excess', secondary: 'ordinary' },
        { label: 'Distressed, gothic, or cursed artwork.', primary: 'chaos', secondary: 'excess' },
        { label: 'An unusual fabric, shape, or fit.', primary: 'ordinary', secondary: 'chaos' },
      ],
    },
    {
      prompt: 'Which sentence sounds most like you?',
      answers: [
        { label: 'I flirt without trying.', primary: 'lust', secondary: 'chaos' },
        { label: 'I hate being ignored.', primary: 'disturbance', secondary: 'chaos' },
        { label: 'More is always better.', primary: 'excess', secondary: 'lust' },
        { label: 'My taste is a little dark.', primary: 'chaos', secondary: 'ordinary' },
        { label: 'I hate dressing like everyone else.', primary: 'ordinary', secondary: 'chaos' },
      ],
    },
    {
      prompt: 'What reaction should your outfit get?',
      answers: [
        { label: '“They look dangerous.”', primary: 'lust', secondary: 'chaos' },
        { label: '“That fit is loud.”', primary: 'disturbance', secondary: 'chaos' },
        { label: '“They’re doing too much—in a good way.”', primary: 'excess', secondary: 'lust' },
        { label: '“What kind of chaos is this?”', primary: 'chaos', secondary: 'excess' },
        { label: '“Where did they even find that?”', primary: 'ordinary', secondary: 'chaos' },
      ],
    },
  ];

  class GPInterrogationRoom extends HTMLElement {
    constructor() {
      super();
      this.answers = Array(QUESTIONS.length).fill(null);
      this.currentQuestion = 0;
      this.isTransitioning = false;
      this.advanceTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.screens = Array.from(this.querySelectorAll('[data-ir-screen]'));
      this.nameInput = this.querySelector('[data-ir-name]');
      this.startButton = this.querySelector('[data-ir-start]');
      this.backButton = this.querySelector('[data-ir-back]');
      this.retakeButton = this.querySelector('[data-ir-retake]');
      this.viewEvidenceButton = this.querySelector('[data-ir-view-evidence]');
      this.mugshotLink = this.querySelector('[data-ir-mugshot]');
      this.questionCount = this.querySelector('[data-ir-question-count]');
      this.questionHeading = this.querySelector('[data-ir-question]');
      this.answersRoot = this.querySelector('[data-ir-answers]');
      this.progress = this.querySelector('[data-ir-progress]');
      this.progressSegments = Array.from(this.querySelectorAll('[data-ir-progress-segment]'));
      this.evidenceRoot = this.querySelector('[data-ir-evidence]');
      this.announcer = this.querySelector('[data-ir-announcer]');

      try {
        this.results = JSON.parse(this.querySelector('[data-ir-results]')?.textContent || '{}');
      } catch (_error) {
        this.results = {};
      }

      this.startButton?.addEventListener('click', () => this.startInterrogation(false));
      this.backButton?.addEventListener('click', () => this.goBack());
      this.retakeButton?.addEventListener('click', () => this.startInterrogation(true));
      this.viewEvidenceButton?.addEventListener('click', () => this.scrollToEvidence());
      this.addEventListener('click', (event) => this.trackProductClick(event));
      this.addEventListener('keydown', (event) => this.handleKeyboard(event));
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnectedCallback() {
      window.clearTimeout(this.advanceTimer);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleVisibilityChange = () => {
      this.classList.toggle('is-page-hidden', document.hidden);
    };

    startInterrogation(isRetake) {
      window.clearTimeout(this.advanceTimer);
      this.answers = Array(QUESTIONS.length).fill(null);
      this.currentQuestion = 0;
      this.isTransitioning = false;
      this.hideEvidenceGroups();
      this.showScreen('quiz');
      this.renderQuestion();
      this.emit('gp:interrogation:start', { version: STORAGE_VERSION, retake: Boolean(isRetake) });
    }

    renderQuestion() {
      const question = QUESTIONS[this.currentQuestion];
      const questionNumber = this.currentQuestion + 1;
      const selectedAnswer = this.answers[this.currentQuestion];

      this.questionCount.textContent = `QUESTION ${String(questionNumber).padStart(2, '0')} OF ${String(QUESTIONS.length).padStart(2, '0')}`;
      this.questionHeading.textContent = question.prompt;
      this.progress.setAttribute('aria-valuenow', String(questionNumber));
      this.progressSegments.forEach((segment, index) => {
        segment.classList.toggle('is-complete', index < questionNumber);
        segment.classList.toggle('is-current', index === questionNumber - 1);
      });
      this.backButton.hidden = this.currentQuestion === 0;
      this.answersRoot.replaceChildren();

      question.answers.forEach((answer, answerIndex) => {
        const button = document.createElement('button');
        const number = document.createElement('span');
        const label = document.createElement('span');
        const marker = document.createElement('span');

        button.type = 'button';
        button.className = 'gp-ir__answer';
        button.dataset.answerIndex = String(answerIndex);
        button.setAttribute('aria-pressed', selectedAnswer === answerIndex ? 'true' : 'false');
        if (selectedAnswer === answerIndex) button.classList.add('is-selected');

        number.className = 'gp-ir__answer-number';
        number.textContent = String(answerIndex + 1).padStart(2, '0');
        number.setAttribute('aria-hidden', 'true');
        label.className = 'gp-ir__answer-label';
        label.textContent = answer.label;
        marker.className = 'gp-ir__answer-marker';
        marker.textContent = selectedAnswer === answerIndex ? 'RECORDED' : 'SELECT';
        marker.setAttribute('aria-hidden', 'true');

        button.append(number, label, marker);
        button.addEventListener('click', () => this.selectAnswer(answerIndex));
        this.answersRoot.append(button);
      });

      this.announce(`${this.questionCount.textContent}. ${question.prompt}`);
      window.requestAnimationFrame(() => this.questionHeading.focus({ preventScroll: true }));
    }

    selectAnswer(answerIndex) {
      if (this.isTransitioning || !Number.isInteger(answerIndex)) return;
      const question = QUESTIONS[this.currentQuestion];
      const answer = question.answers[answerIndex];
      if (!answer) return;

      this.answers[this.currentQuestion] = answerIndex;
      this.isTransitioning = true;

      this.answersRoot.querySelectorAll('.gp-ir__answer').forEach((button, index) => {
        const selected = index === answerIndex;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const marker = button.querySelector('.gp-ir__answer-marker');
        if (marker) marker.textContent = selected ? 'RECORDED' : 'SELECT';
        button.disabled = true;
      });
      this.backButton.disabled = true;

      this.emit('gp:interrogation:answer', {
        question: this.currentQuestion + 1,
        answerPosition: answerIndex + 1,
        primaryKey: answer.primary,
      });

      this.advanceTimer = window.setTimeout(() => {
        this.isTransitioning = false;
        this.backButton.disabled = false;
        if (this.currentQuestion < QUESTIONS.length - 1) {
          this.currentQuestion += 1;
          this.renderQuestion();
        } else {
          this.completeInterrogation();
        }
      }, this.reducedMotion ? 50 : ADVANCE_DELAY);
    }

    goBack() {
      if (this.isTransitioning || this.currentQuestion === 0) return;
      window.clearTimeout(this.advanceTimer);
      this.currentQuestion -= 1;
      this.renderQuestion();
    }

    completeInterrogation() {
      const scores = Object.fromEntries(RESULT_KEYS.map((key) => [key, 0]));

      this.answers.forEach((answerIndex, questionIndex) => {
        const answer = QUESTIONS[questionIndex].answers[answerIndex];
        scores[answer.primary] += 3;
        scores[answer.secondary] += 1;
      });

      const chargeKey = this.resolveWinner(scores);
      const sortedScores = Object.values(scores).sort((a, b) => b - a);
      const scoreDifference = sortedScores[0] - sortedScores[1];
      const threatLevel = Math.min(96, Math.max(68, 68 + scoreDifference * 2));
      const result = this.results[chargeKey];
      const subjectName = this.getSubjectName();

      if (!result) return;

      this.querySelector('[data-ir-result-name]').textContent = subjectName;
      this.querySelector('[data-ir-result-charge]').textContent = result.charge;
      this.querySelector('[data-ir-result-threat]').textContent = `${threatLevel}%`;
      this.querySelector('[data-ir-result-status]').textContent = result.status;
      this.querySelector('[data-ir-result-description]').textContent = result.description;

      const hasProducts = this.showEvidenceGroup(chargeKey);
      this.viewEvidenceButton.hidden = !hasProducts;
      this.updateMugshotUrl(chargeKey, subjectName);
      this.showScreen('result');

      const completedResult = {
        version: STORAGE_VERSION,
        subjectName,
        chargeKey,
        chargeTitle: result.charge,
        status: result.status,
        threatLevel,
        completedAt: new Date().toISOString(),
      };

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(completedResult));
      } catch (_error) {
        // Storage can be unavailable in private or restricted browsing contexts.
      }

      this.emit('gp:interrogation:complete', { chargeKey, chargeTitle: result.charge, threatLevel });
      this.announce(`Your style verdict. ${result.charge}. Style match ${threatLevel} percent. Status: ${result.status}.`);
      window.requestAnimationFrame(() => this.querySelector('[data-ir-screen="result"] h2')?.focus({ preventScroll: true }));
    }

    resolveWinner(scores) {
      const highestScore = Math.max(...Object.values(scores));
      const tiedKeys = RESULT_KEYS.filter((key) => scores[key] === highestScore);
      if (tiedKeys.length === 1) return tiedKeys[0];

      for (const questionIndex of [4, 2]) {
        const answerIndex = this.answers[questionIndex];
        const tieBreakerKey = QUESTIONS[questionIndex].answers[answerIndex].primary;
        if (tiedKeys.includes(tieBreakerKey)) return tieBreakerKey;
      }

      return RESULT_KEYS.find((key) => tiedKeys.includes(key));
    }

    getSubjectName() {
      const enteredName = this.nameInput ? this.nameInput.value : this.dataset.customerName;
      const normalizedName = String(enteredName || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 30);
      return normalizedName ? normalizedName.toLocaleUpperCase() : 'UNKNOWN SUBJECT';
    }

    showScreen(screenName) {
      this.screens.forEach((screen) => {
        const isActive = screen.dataset.irScreen === screenName;
        screen.hidden = !isActive;
        screen.classList.remove('is-entering');
        if (isActive && !this.reducedMotion) {
          window.requestAnimationFrame(() => screen.classList.add('is-entering'));
        }
      });

      if (screenName !== 'result') this.evidenceRoot.hidden = true;
      this.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }

    showEvidenceGroup(chargeKey) {
      let hasProducts = false;
      this.querySelectorAll('[data-ir-product-group]').forEach((group) => {
        const isCurrent = group.dataset.chargeProducts === chargeKey;
        const containsProducts = Boolean(group.querySelector('[data-ir-product-id]'));
        group.hidden = !isCurrent || !containsProducts;
        if (isCurrent && containsProducts) hasProducts = true;
      });
      this.evidenceRoot.hidden = !hasProducts;
      return hasProducts;
    }

    hideEvidenceGroups() {
      this.evidenceRoot.hidden = true;
      this.querySelectorAll('[data-ir-product-group]').forEach((group) => {
        group.hidden = true;
      });
    }

    scrollToEvidence() {
      if (this.evidenceRoot.hidden) return;
      this.evidenceRoot.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
      window.requestAnimationFrame(() => this.evidenceRoot.querySelector('a[href]')?.focus({ preventScroll: true }));
    }

    updateMugshotUrl(chargeKey, subjectName) {
      if (!this.mugshotLink) return;
      const configuredUrl = this.dataset.mugshotUrl;
      if (!configuredUrl) return;

      try {
        const destination = new URL(configuredUrl, window.location.origin);
        destination.searchParams.set('charge', chargeKey);
        destination.searchParams.set('name', subjectName);
        this.mugshotLink.href = destination.toString();
      } catch (_error) {
        this.mugshotLink.hidden = true;
      }
    }

    trackProductClick(event) {
      const link = event.target.closest('[data-ir-product-link], .product-card__link');
      if (!link || !this.contains(link)) return;
      const productCard = link.closest('[data-ir-product-id]');
      const productGroup = link.closest('[data-charge-products]');
      if (!productCard || !productGroup) return;

      this.emit('gp:interrogation:product-click', {
        chargeKey: productGroup.dataset.chargeProducts,
        productId: productCard.dataset.irProductId,
      });
    }

    handleKeyboard(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const quizScreen = this.querySelector('[data-ir-screen="quiz"]');
      if (!quizScreen || quizScreen.hidden || this.isTransitioning) return;
      if (event.target.matches('input, textarea, select')) return;

      if (/^[1-5]$/.test(event.key)) {
        event.preventDefault();
        this.selectAnswer(Number(event.key) - 1);
      } else if (event.key === 'ArrowLeft' && this.currentQuestion > 0) {
        event.preventDefault();
        this.goBack();
      }
    }

    announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = '';
      window.setTimeout(() => {
        this.announcer.textContent = message;
      }, 30);
    }

    emit(eventName, detail) {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName.replaceAll(':', '_'), ...detail });
      }
    }
  }

  if (!customElements.get('gp-interrogation-room')) {
    customElements.define('gp-interrogation-room', GPInterrogationRoom);
  }
})();
